import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Session } from '@buildiary/core';
import { useT } from '../app/context';
import { ApiFailure, errorText, request } from '../lib/api';
import { BrandTile, Icon, Wordmark } from '../ui/Icon';
import { Button, Field, Input } from '../ui/kit';

/** POST /api/auth/set-password is a server extra (docs/CONTRACT-NOTES.md, Server #5); called through request() with a cast. */
const setPassword = request as unknown as (endpoint: 'POST /api/auth/set-password', opts: { body: { token: string; password: string } }) => Promise<Session>;

/** /set-password?token=… — a new owner (or person) chooses a password from the link they were sent. */
export function SetPasswordPage() {
  const t = useT();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [fe, setFe] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const f: Record<string, string> = {};
    if (pw.length < 8) f.pw = 'Use at least 8 characters.';
    if (pw !== pw2) f.pw2 = 'The two passwords are not the same.';
    setFe(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setErr(null);
    try {
      await setPassword('POST /api/auth/set-password', { body: { token, password: pw } });
      window.location.assign('/');
    } catch (e) {
      setErr(e instanceof ApiFailure && e.status === 410 ? 'This link was already used or is too old. Ask SMH Global or your workspace owner for a new one.' : errorText(e));
      setBusy(false);
    }
  };
  return (
    <main className="signin">
      <section className="signin-hero">
        <div className="row" style={{ gap: 12 }}><BrandTile size={44} /><Wordmark width={136} /></div>
        <div className="stack hero-copy" style={{ marginTop: 56, gap: 18, maxWidth: 520 }}>
          <span className="kicker">{t('si.kickerLive')}</span>
          <h1>{t('si.headline')}</h1>
        </div>
        <span style={{ marginTop: 'auto', fontSize: 13, color: 'rgba(255,255,255,.72)' }}>{t('si.endorse')}</span>
      </section>
      <section className="signin-main" style={{ maxWidth: 640 }}>
        <div className="stack" style={{ gap: 6 }}>
          <h2>Choose your password</h2>
          <p className="muted" style={{ fontSize: 15 }}>You will use it with your email to sign in to Buildiary.</p>
        </div>
        {!token ? (
          <div className="alert danger" role="alert"><Icon name="warn" size={16} />This page needs the link from your email or WhatsApp message.</div>
        ) : (
          <form className="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
            {err && <div className="alert danger" role="alert"><Icon name="warn" size={16} />{err}</div>}
            <Field label="New password" error={fe.pw} hint="At least 8 characters"><Input type="password" autoComplete="new-password" value={pw} invalid={!!fe.pw} onChange={(e) => setPw(e.target.value)} /></Field>
            <Field label="Type it again" error={fe.pw2}><Input type="password" autoComplete="new-password" value={pw2} invalid={!!fe.pw2} onChange={(e) => setPw2(e.target.value)} /></Field>
            <Button type="submit" variant="primary" loading={busy} style={{ minHeight: 46 }}>Save and sign in</Button>
          </form>
        )}
      </section>
    </main>
  );
}
